import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { loginUser, clearError } from '../store/slices/authSlice';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';
import {
  Sparkles,
  LayoutDashboard,
  ArrowRight,
  AlertCircle,
  KeyRound,
  FileText,
  ShieldCheck,
  Settings2,
  Users,
  ClipboardList,
  Handshake,
  Wallet,
  User,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../utils/cn';

/** Development-only quick-login buttons (one per seeded demo role). */
const DEMO_ROLES: Array<{ label: string; email: string; icon: LucideIcon; className: string }> = [
  { label: 'Admin', email: 'admin@agency.com', icon: ShieldCheck, className: 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200' },
  { label: 'Operations', email: 'operations@agency.com', icon: Settings2, className: 'bg-violet-50 hover:bg-violet-100 text-violet-700 border-violet-200' },
  { label: 'Pod Lead', email: 'podlead@agency.com', icon: Users, className: 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200' },
  { label: 'Project Manager', email: 'pm@agency.com', icon: ClipboardList, className: 'bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200' },
  { label: 'Account Manager', email: 'account@agency.com', icon: Handshake, className: 'bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200' },
  { label: 'Finance', email: 'finance@agency.com', icon: Wallet, className: 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200' },
  { label: 'Team Member', email: 'team@agency.com', icon: User, className: 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200' },
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Enhanced error persistence - prevent any accidental clearing
  const [persistentError, setPersistentError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      setPersistentError(error);
      console.log(`Login: Error set and persisted - "${error}"`);
    } else if (!error && persistentError) {
      // Only clear persistent error if it was intentionally cleared
      console.log(`Login: Error cleared from Redux, checking if intentional...`);
    }
  }, [error, persistentError]);

  // Clear persistent error only when user starts typing or submits form
  const clearPersistentError = useCallback(() => {
    console.log(`Login: Intentionally clearing persistent error`);
    setPersistentError(null);
    dispatch(clearError());
  }, [dispatch]);

  // Don't clear errors on mount - let them persist until user starts typing

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Clear errors when user starts typing (only if they actually type something)
    if ((error || persistentError) && value.length > 0) {
      clearPersistentError();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginUser(formData));
  };

  const handleQuickLogin = (demoEmail: string) => {
    setFormData({
      email: demoEmail,
      password: 'password'
    });
    dispatch(loginUser({ email: demoEmail, password: 'password' }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-200/30 to-purple-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-emerald-200/20 to-cyan-200/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-amber-100/30 to-orange-100/20 rounded-full blur-3xl" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwMDAwMDA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-60" />

      <div className="relative z-10 w-full max-w-md px-4 py-8 sm:py-12">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-xl shadow-indigo-500/25 mb-6">
            <LayoutDashboard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 bg-clip-text text-transparent">
            Fulcrum
          </h1>
          <p className="mt-2 text-sm text-slate-500 font-medium">
            Multi management platform
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-900/5 border border-white/60 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <h2 className="text-lg font-semibold text-slate-800">
                Welcome back
              </h2>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {(error || persistentError) && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error || persistentError}</p>
                </div>
              )}

              <div className="space-y-4">
                <Input
                  label="Email address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                />

                <Input
                  label="Password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0 transition-colors"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    Remember me
                  </span>
                </label>

                <a href="#" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
                  Forgot password?
                </a>
              </div>

              <Button
                type="submit"
                className="w-full h-11 text-base font-medium bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-200"
                isLoading={isLoading}
                disabled={!formData.email || !formData.password}
              >
                <span className="flex items-center gap-2">
                  Sign in
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 text-sm text-slate-400 bg-white">or continue with</span>
                </div>
              </div>

              <GoogleLoginButton />
            </form>
          </div>
        </div>

        {/* Demo Credentials - Only show in development */}
        {import.meta.env.MODE === 'development' && (
          <div className="mt-6">
            <div className="bg-white/60 backdrop-blur-sm border border-indigo-100/60 rounded-xl p-5 shadow-lg shadow-slate-900/5">
              <div className="flex items-center justify-center gap-2 mb-4">
                <KeyRound className="w-4 h-4 text-amber-500" aria-hidden="true" />
                <h4 className="text-sm font-semibold text-slate-700">Demo Access</h4>
              </div>

              {/* Quick Login Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {DEMO_ROLES.map(({ label, email, icon: Icon, className }, index) => (
                  <button
                    key={email}
                    type="button"
                    onClick={() => handleQuickLogin(email)}
                    className={cn(
                      'px-3 py-2.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-60',
                      className,
                      // The odd one out spans the full row.
                      index === DEMO_ROLES.length - 1 && DEMO_ROLES.length % 2 === 1 && 'col-span-2',
                    )}
                    disabled={isLoading}
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                      {label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Manual Credentials */}
              <details className="text-left group">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-indigo-600 transition-colors flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                  View manual credentials
                </summary>
                <div className="text-xs text-slate-600 space-y-1 mt-3 pl-5 border-l-2 border-slate-100">
                  <div><span className="font-semibold text-slate-700">Admin:</span> admin@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">Operations:</span> operations@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">Pod Lead:</span> podlead@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">PM:</span> pm@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">Account:</span> account@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">Finance:</span> finance@agency.com / password</div>
                  <div><span className="font-semibold text-slate-700">Team:</span> team@agency.com / password</div>
                </div>
              </details>
            </div>
            <p className="text-center text-xs text-slate-400 mt-3">
              Select a role to experience role-based access control
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
